import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Plus, Trash2, ChevronUp, ChevronDown, Eye, Save, Sparkles, Globe,
  Layout, Type, Image, Video, List, Star, DollarSign, HelpCircle,
  ArrowRight, Loader2, FileText, Copy, Code, Palette, Settings,
  GripVertical, PenLine, Monitor, Smartphone, ExternalLink, CheckCircle2,
  BarChart2, ArrowLeft, Zap, Share2, QrCode, TrendingUp, Users, Globe2,
  MessageSquare, Send, RotateCcw, Paperclip, X
} from 'lucide-react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTION_TYPES = [
  { type: 'hero', label: 'Hero Banner', icon: Layout, desc: 'Large banner with heading & CTA' },
  { type: 'features', label: 'Features', icon: List, desc: 'Feature grid with icons' },
  { type: 'cta', label: 'Call to Action', icon: ArrowRight, desc: 'Conversion-focused CTA block' },
  { type: 'lead_form', label: 'Lead Form', icon: Send, desc: 'Capture leads — saves to CRM' },
  { type: 'content', label: 'Content Block', icon: Type, desc: 'Rich text content area' },
  { type: 'testimonials', label: 'Testimonials', icon: Star, desc: 'Customer quotes & reviews' },
  { type: 'pricing', label: 'Pricing', icon: DollarSign, desc: 'Pricing tiers comparison' },
  { type: 'stats', label: 'Statistics', icon: BarChart2, desc: 'Numbers & metrics showcase' },
  { type: 'faq', label: 'FAQ', icon: HelpCircle, desc: 'Frequently asked questions' },
  { type: 'gallery', label: 'Image Gallery', icon: Image, desc: 'Photo or media grid' },
  { type: 'html_raw', label: 'HTML / Code', icon: Code, desc: 'Paste your own HTML — full control' },
  { type: 'footer', label: 'Footer', icon: FileText, desc: 'Page footer with links' },
];

const TEMPLATES = [
  { id: 'blank', name: 'Blank Canvas', desc: 'Start from scratch', sections: [] },
  { id: 'product_launch', name: 'Product Launch', desc: 'Hero + Features + CTA + Pricing', sections: [
    { type: 'hero', heading: 'Introducing [Product Name]', subheading: 'The next generation solution for your business', content: 'Transform how you work with our powerful platform.', button_text: 'Get Started Free', button_url: '#signup', background: '#0f172a', text_color: '#ffffff', layout: 'center' },
    { type: 'features', heading: 'Why Choose Us', subheading: 'Built for modern teams', items: [
      { title: 'Lightning Fast', description: 'Optimized for speed and performance', icon: 'Zap' },
      { title: 'Secure & Reliable', description: 'Enterprise-grade security built-in', icon: 'Shield' },
      { title: 'Easy Integration', description: 'Connect with your favorite tools', icon: 'Link2' },
    ], background: '#ffffff', text_color: '#1e293b', layout: 'center' },
    { type: 'cta', heading: 'Ready to Transform Your Business?', subheading: 'Join thousands of companies already using our platform', button_text: 'Start Free Trial', button_url: '#signup', background: '#10B981', text_color: '#ffffff', layout: 'center' },
  ]},
  { id: 'lead_capture', name: 'Lead Capture', desc: 'Focused on collecting leads', sections: [
    { type: 'hero', heading: 'Get Your Free [Resource]', subheading: 'Discover the secrets to growing your business', content: 'Enter your email to receive our exclusive guide instantly.', button_text: 'Download Now', button_url: '#form', background: 'linear-gradient(135deg, #1e293b, #0f172a)', text_color: '#ffffff', layout: 'center' },
    { type: 'stats', heading: 'Trusted by Industry Leaders', items: [
      { title: '10,000+', description: 'Active Users' },
      { title: '98%', description: 'Satisfaction Rate' },
      { title: '50+', description: 'Countries' },
    ], background: '#f8fafc', text_color: '#1e293b', layout: 'center' },
    { type: 'testimonials', heading: 'What Our Customers Say', items: [
      { title: 'John D., CEO', description: 'This product changed how we do business. Highly recommended!' },
      { title: 'Sarah M., CMO', description: 'The best investment we made this year. ROI was immediate.' },
    ], background: '#ffffff', text_color: '#1e293b', layout: 'center' },
    { type: 'cta', heading: 'Don\'t Miss Out', button_text: 'Get Free Access', button_url: '#form', background: '#3B82F6', text_color: '#ffffff', layout: 'center' },
  ]},
  { id: 'saas_landing', name: 'SaaS Platform', desc: 'Hero + Features + Pricing + FAQ', sections: [
    { type: 'hero', heading: 'The All-in-One Platform Your Team Needs', subheading: 'Streamline workflows, boost productivity, and scale faster', button_text: 'Start 14-Day Free Trial', button_url: '#signup', background: '#0f172a', text_color: '#ffffff', layout: 'center' },
    { type: 'features', heading: 'Everything You Need', items: [
      { title: 'Automation', description: 'Automate repetitive tasks and save hours every week', icon: 'Zap' },
      { title: 'Analytics', description: 'Deep insights into your business performance', icon: 'BarChart2' },
      { title: 'Collaboration', description: 'Real-time collaboration with your entire team', icon: 'Users' },
      { title: 'Integrations', description: 'Connect with 100+ popular tools', icon: 'Link2' },
    ], background: '#ffffff', text_color: '#1e293b', layout: 'center' },
    { type: 'pricing', heading: 'Simple, Transparent Pricing', items: [
      { title: 'Starter', description: '$29/mo — For small teams', icon: '1-10 users' },
      { title: 'Professional', description: '$79/mo — Growing businesses', icon: '11-50 users' },
      { title: 'Enterprise', description: 'Custom — Unlimited scale', icon: 'Unlimited' },
    ], background: '#f8fafc', text_color: '#1e293b', layout: 'center' },
    { type: 'faq', heading: 'Frequently Asked Questions', items: [
      { title: 'How does the free trial work?', description: 'You get full access for 7 days with 100 credits included. Top up any time to keep generating — no credit card required to start.' },
      { title: 'Can I cancel anytime?', description: 'Yes, you can cancel your subscription at any time with no penalties.' },
    ], background: '#ffffff', text_color: '#1e293b', layout: 'center' },
  ]},
];

const DEFAULT_SECTION = (type) => ({
  type,
  heading: '', subheading: '', content: '',
  items: [], button_text: '', button_url: '#',
  image_url: '', video_url: '', layout: 'center',
  background: '#ffffff', text_color: '#1e293b',
});


// ═══════ Section Editor ═══════
function SectionEditor({ section, index, onUpdate, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const meta = SECTION_TYPES.find(s => s.type === section.type) || { label: section.type, icon: Layout };
  const Icon = meta.icon;

  const updateField = (field, value) => onUpdate(index, { ...section, [field]: value });

  const addItem = () => updateField('items', [...(section.items || []), { title: '', description: '', icon: '' }]);
  const updateItem = (i, field, val) => {
    const items = [...(section.items || [])];
    items[i] = { ...items[i], [field]: val };
    updateField('items', items);
  };
  const removeItem = (i) => updateField('items', (section.items || []).filter((_, j) => j !== i));

  return (
    <Card className="bg-white border-gray-200 shadow-sm" data-testid={`section-editor-${index}`}>
      <CardContent className="p-0">
        {/* Section Header */}
        <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 border-b border-gray-200 rounded-t-xl">
          <GripVertical className="h-4 w-4 text-gray-400 cursor-grab" />
          <div className="w-6 h-6 rounded bg-emerald-100 flex items-center justify-center">
            <Icon className="h-3 w-3 text-emerald-600" />
          </div>
          <span className="text-xs font-semibold text-gray-700 flex-1">{meta.label}</span>
          <div className="flex items-center gap-0.5">
            <button onClick={() => onMoveUp(index)} disabled={isFirst} className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"><ChevronUp className="h-3.5 w-3.5 text-gray-500" /></button>
            <button onClick={() => onMoveDown(index)} disabled={isLast} className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"><ChevronDown className="h-3.5 w-3.5 text-gray-500" /></button>
            <button onClick={() => onRemove(index)} className="p-1 hover:bg-red-100 rounded ml-1" data-testid={`remove-section-${index}`}><Trash2 className="h-3.5 w-3.5 text-red-400" /></button>
          </div>
        </div>

        {/* Section Fields */}
        <div className="p-3 space-y-3">
          {section.type === 'html_raw' ? (
            <>
              <div className="p-2 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-700">
                <b>Raw HTML mode.</b> Paste any HTML — including <code>&lt;style&gt;</code>, <code>&lt;script&gt;</code>, custom forms. To capture leads, add <code>data-konnect-form="1"</code> to your <code>&lt;form&gt;</code> tag — it will auto-POST to your CRM.
              </div>
              <div>
                <Label className="text-[10px] text-gray-500">HTML content</Label>
                <Textarea
                  className="text-[11px] mt-0.5 min-h-[200px] font-mono"
                  value={section.content || ''}
                  placeholder='<div style="text-align:center;padding:60px"><h1>Your custom HTML</h1></div>'
                  onChange={e => updateField('content', e.target.value)}
                  data-testid={`html-content-${index}`}
                />
              </div>
            </>
          ) : section.type === 'lead_form' ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-gray-500">Heading</Label>
                  <Input className="h-8 text-xs mt-0.5" value={section.heading || ''} placeholder="Get in touch" onChange={e => updateField('heading', e.target.value)} />
                </div>
                <div>
                  <Label className="text-[10px] text-gray-500">Subheading</Label>
                  <Input className="h-8 text-xs mt-0.5" value={section.subheading || ''} placeholder="We'll respond within 24h" onChange={e => updateField('subheading', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-gray-500">Submit button text</Label>
                  <Input className="h-8 text-xs mt-0.5" value={section.button_text || ''} placeholder="Send message" onChange={e => updateField('button_text', e.target.value)} />
                </div>
                <div>
                  <Label className="text-[10px] text-gray-500">After-submit URL (optional)</Label>
                  <Input className="h-8 text-xs mt-0.5" value={section.button_url || ''} placeholder="https://your-site/thanks" onChange={e => updateField('button_url', e.target.value)} />
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-gray-500">Success message</Label>
                <Input className="h-8 text-xs mt-0.5" value={section.success_message || ''} placeholder="Thanks! We'll be in touch." onChange={e => updateField('success_message', e.target.value)} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-[10px] text-gray-500">Fields ({(section.fields || []).length || 'default 4'})</Label>
                  <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1 text-emerald-600"
                    onClick={() => updateField('fields', [...(section.fields || [
                      { name: 'name', label: 'Your name', type: 'text', required: true },
                      { name: 'email', label: 'Email', type: 'email', required: true },
                      { name: 'phone', label: 'Phone', type: 'tel', required: false },
                      { name: 'message', label: 'Message', type: 'textarea', required: false },
                    ]), { name: 'field' + ((section.fields || []).length + 1), label: 'New field', type: 'text', required: false }])}>
                    <Plus className="h-3 w-3" />Add field
                  </Button>
                </div>
                {(section.fields || []).length === 0 ? (
                  <p className="text-[10px] text-gray-400 italic">Using default: name, email, phone, message. Click "Add field" to customize.</p>
                ) : (
                  <div className="space-y-1.5 max-h-[160px] overflow-y-auto">
                    {(section.fields || []).map((f, fi) => (
                      <div key={fi} className="grid grid-cols-12 gap-1 items-center p-1.5 bg-gray-50 rounded">
                        <Input className="h-6 text-[10px] col-span-3" placeholder="name" value={f.name || ''}
                          onChange={e => { const fs = [...(section.fields || [])]; fs[fi] = { ...fs[fi], name: e.target.value }; updateField('fields', fs); }} />
                        <Input className="h-6 text-[10px] col-span-4" placeholder="Label" value={f.label || ''}
                          onChange={e => { const fs = [...(section.fields || [])]; fs[fi] = { ...fs[fi], label: e.target.value }; updateField('fields', fs); }} />
                        <Select value={f.type || 'text'} onValueChange={v => { const fs = [...(section.fields || [])]; fs[fi] = { ...fs[fi], type: v }; updateField('fields', fs); }}>
                          <SelectTrigger className="h-6 text-[10px] col-span-3"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['text', 'email', 'tel', 'number', 'url', 'textarea'].map(t => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <label className="flex items-center gap-1 text-[10px] col-span-1">
                          <input type="checkbox" checked={!!f.required} onChange={e => { const fs = [...(section.fields || [])]; fs[fi] = { ...fs[fi], required: e.target.checked }; updateField('fields', fs); }} className="h-3 w-3" />
                          *
                        </label>
                        <button onClick={() => updateField('fields', (section.fields || []).filter((_, j) => j !== fi))} className="col-span-1 p-0.5 hover:bg-red-100 rounded">
                          <Trash2 className="h-3 w-3 text-red-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Background + Layout */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px] text-gray-500">Background</Label>
                  <div className="flex items-center gap-1 mt-0.5">
                    <input type="color" value={section.background?.startsWith('#') ? section.background : '#ffffff'}
                      onChange={e => updateField('background', e.target.value)} className="w-7 h-7 rounded border cursor-pointer" />
                    <Input className="h-7 text-[10px] flex-1" value={section.background || ''} onChange={e => updateField('background', e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] text-gray-500">Text Color</Label>
                  <div className="flex items-center gap-1 mt-0.5">
                    <input type="color" value={section.text_color || '#1e293b'}
                      onChange={e => updateField('text_color', e.target.value)} className="w-7 h-7 rounded border cursor-pointer" />
                    <Input className="h-7 text-[10px] flex-1" value={section.text_color || ''} onChange={e => updateField('text_color', e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] text-gray-500">Layout</Label>
                  <Select value={section.layout || 'center'} onValueChange={v => updateField('layout', v)}>
                    <SelectTrigger className="h-7 text-[10px] mt-0.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="center">Center</SelectItem>
                      <SelectItem value="left">Left</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          ) : (
          <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-gray-500">Heading</Label>
              <Input className="h-8 text-xs mt-0.5" value={section.heading || ''} placeholder="Section heading"
                onChange={e => updateField('heading', e.target.value)} data-testid={`heading-${index}`} />
            </div>
            <div>
              <Label className="text-[10px] text-gray-500">Subheading</Label>
              <Input className="h-8 text-xs mt-0.5" value={section.subheading || ''} placeholder="Optional subheading"
                onChange={e => updateField('subheading', e.target.value)} />
            </div>
          </div>

          {['hero', 'content', 'cta'].includes(section.type) && (
            <div>
              <Label className="text-[10px] text-gray-500">Content / Body</Label>
              <Textarea className="text-xs mt-0.5 min-h-[60px]" value={section.content || ''} placeholder="Body text or HTML..."
                onChange={e => updateField('content', e.target.value)} />
            </div>
          )}

          {['hero', 'cta'].includes(section.type) && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-gray-500">Button Text</Label>
                <Input className="h-8 text-xs mt-0.5" value={section.button_text || ''} placeholder="Get Started"
                  onChange={e => updateField('button_text', e.target.value)} />
              </div>
              <div>
                <Label className="text-[10px] text-gray-500">Button URL</Label>
                <Input className="h-8 text-xs mt-0.5" value={section.button_url || ''} placeholder="#signup"
                  onChange={e => updateField('button_url', e.target.value)} />
              </div>
            </div>
          )}

          {['hero', 'gallery', 'content'].includes(section.type) && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-gray-500">Image URL</Label>
                <Input className="h-8 text-xs mt-0.5" value={section.image_url || ''} placeholder="https://..."
                  onChange={e => updateField('image_url', e.target.value)} />
              </div>
              <div>
                <Label className="text-[10px] text-gray-500">Video URL</Label>
                <Input className="h-8 text-xs mt-0.5" value={section.video_url || ''} placeholder="https://youtube.com/..."
                  onChange={e => updateField('video_url', e.target.value)} />
              </div>
            </div>
          )}

          {/* Style Controls */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] text-gray-500">Background</Label>
              <div className="flex items-center gap-1 mt-0.5">
                <input type="color" value={section.background?.startsWith('#') ? section.background : '#ffffff'}
                  onChange={e => updateField('background', e.target.value)} className="w-7 h-7 rounded border cursor-pointer" />
                <Input className="h-7 text-[10px] flex-1" value={section.background || ''} onChange={e => updateField('background', e.target.value)} />
              </div>
            </div>
            <div>
              <Label className="text-[10px] text-gray-500">Text Color</Label>
              <div className="flex items-center gap-1 mt-0.5">
                <input type="color" value={section.text_color || '#1e293b'}
                  onChange={e => updateField('text_color', e.target.value)} className="w-7 h-7 rounded border cursor-pointer" />
                <Input className="h-7 text-[10px] flex-1" value={section.text_color || ''} onChange={e => updateField('text_color', e.target.value)} />
              </div>
            </div>
            <div>
              <Label className="text-[10px] text-gray-500">Layout</Label>
              <Select value={section.layout || 'center'} onValueChange={v => updateField('layout', v)}>
                <SelectTrigger className="h-7 text-[10px] mt-0.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Items (features, testimonials, pricing, stats, faq) */}
          {['features', 'testimonials', 'pricing', 'stats', 'faq', 'gallery'].includes(section.type) && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-[10px] text-gray-500">Items ({(section.items || []).length})</Label>
                <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1 text-emerald-600" onClick={addItem} data-testid={`add-item-${index}`}>
                  <Plus className="h-3 w-3" />Add
                </Button>
              </div>
              <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                {(section.items || []).map((item, i) => (
                  <div key={i} className="flex gap-1.5 items-start p-1.5 bg-gray-50 rounded">
                    <div className="flex-1 grid grid-cols-2 gap-1">
                      <Input className="h-6 text-[10px]" placeholder="Title" value={item.title || ''}
                        onChange={e => updateItem(i, 'title', e.target.value)} />
                      <Input className="h-6 text-[10px]" placeholder="Description" value={item.description || ''}
                        onChange={e => updateItem(i, 'description', e.target.value)} />
                    </div>
                    <button onClick={() => removeItem(i)} className="p-0.5 hover:bg-red-100 rounded shrink-0 mt-0.5">
                      <Trash2 className="h-3 w-3 text-red-400" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


// ═══════ Live Preview ═══════
// Reusable iframe-based renderer for raw HTML so platform Tailwind/global
// styles never bleed into user-pasted templates.
function HtmlIframe({ html, minHeight = 200, testid }) {
  const [height, setHeight] = useState(minHeight);
  const srcDoc = (() => {
    if (!html) return '<!doctype html><html><body style="margin:0;padding:2rem;font-family:system-ui;color:#94a3b8;text-align:center">Empty HTML</body></html>';
    const hasHtmlTag = /<html[\s>]/i.test(html);
    const inner = hasHtmlTag ? html : `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head><body style="margin:0">${html}</body></html>`;
    const sizer = `<script>(function(){function s(){try{var h=Math.max(document.body.scrollHeight,document.documentElement.scrollHeight,document.body.offsetHeight);parent.postMessage({type:'klp-prev-height',height:h},'*');}catch(e){}}window.addEventListener('load',s);setTimeout(s,150);setTimeout(s,800);if(window.ResizeObserver){try{new ResizeObserver(s).observe(document.body);}catch(e){}}})();</script>`;
    if (/<\/body>/i.test(inner)) return inner.replace(/<\/body>/i, sizer + '</body>');
    return inner + sizer;
  })();
  useEffect(() => {
    function onMsg(e) {
      if (!e.data || e.data.type !== 'klp-prev-height') return;
      const h = Math.max(minHeight, Math.min(20000, Number(e.data.height) || minHeight));
      setHeight(h);
    }
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [minHeight]);
  return (
    <iframe title="HTML preview" srcDoc={srcDoc} sandbox="allow-scripts allow-same-origin"
      style={{ width: '100%', height: height + 'px', border: 'none', display: 'block', background: '#fff' }}
      data-testid={testid || 'preview-html-iframe'} />
  );
}

function LivePreview({ sections, theme, rawHtml }) {
  // Full HTML override mode — render exclusively from the raw_html_override blob
  if (rawHtml && rawHtml.trim()) {
    return (
      <div className="bg-white rounded-xl overflow-hidden shadow-inner border border-gray-200" data-testid="live-preview-html">
        <HtmlIframe html={rawHtml} minHeight={400} testid="live-preview-iframe" />
      </div>
    );
  }
  const renderSection = (section, i) => {
    const bg = section.background || '#ffffff';
    const txt = section.text_color || '#1e293b';
    const align = section.layout === 'left' ? 'text-left' : section.layout === 'right' ? 'text-right' : 'text-center';

    switch (section.type) {
      case 'html_raw':
        return (
          <div key={i} style={{ background: bg }} className="px-0 py-0">
            <HtmlIframe html={section.content || ''} minHeight={120} testid={`section-html-iframe-${i}`} />
          </div>
        );
      case 'lead_form':
        return (
          <div key={i} style={{ background: bg, color: txt }} className="py-12 px-6">
            <div className={cn('max-w-md mx-auto', align)}>
              {section.heading && <h2 className="text-2xl font-bold mb-2">{section.heading}</h2>}
              {section.subheading && <p className="opacity-80 mb-5 text-sm">{section.subheading}</p>}
              <div className="space-y-2 text-left">
                {((section.fields && section.fields.length) ? section.fields : [
                  { name: 'name', label: 'Your name', type: 'text' },
                  { name: 'email', label: 'Email', type: 'email' },
                  { name: 'phone', label: 'Phone', type: 'tel' },
                  { name: 'message', label: 'Message', type: 'textarea' },
                ]).map((f, j) => (
                  <div key={j}>
                    <label className="block text-xs font-medium mb-1 opacity-80">{f.label}{f.required ? ' *' : ''}</label>
                    {f.type === 'textarea'
                      ? <textarea disabled rows={3} className="w-full px-2 py-1.5 rounded border border-gray-300 bg-white/10 text-xs" placeholder="(preview)" />
                      : <input disabled type={f.type || 'text'} className="w-full px-2 py-1.5 rounded border border-gray-300 bg-white/10 text-xs" placeholder="(preview)" />}
                  </div>
                ))}
                <button disabled className="w-full px-4 py-2.5 rounded-lg text-white font-semibold mt-2"
                  style={{ backgroundColor: theme?.primary_color || '#10B981' }}>
                  {section.button_text || 'Submit'}
                </button>
              </div>
            </div>
          </div>
        );
      case 'hero':
        return (
          <div key={i} style={{ background: bg, color: txt }} className="py-16 px-6">
            <div className={cn("max-w-3xl mx-auto", align)}>
              {section.heading && <h1 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4 leading-tight">{section.heading}</h1>}
              {section.subheading && <p className="text-lg opacity-80 mb-4">{section.subheading}</p>}
              {section.content && <p className="opacity-70 mb-6 max-w-xl mx-auto">{section.content}</p>}
              {section.button_text && (
                <a href={section.button_url || '#'} style={{ backgroundColor: theme?.primary_color || '#10B981' }}
                  className="inline-block px-8 py-3 rounded-lg text-white font-semibold text-sm hover:opacity-90 transition">{section.button_text}</a>
              )}
              {section.image_url && <img src={section.image_url} alt="" className="mt-8 rounded-xl max-w-full mx-auto shadow-lg" />}
            </div>
          </div>
        );
      case 'features':
        return (
          <div key={i} style={{ background: bg, color: txt }} className="py-12 px-6">
            <div className={cn("max-w-4xl mx-auto", align)}>
              {section.heading && <h2 className="text-2xl font-bold mb-2">{section.heading}</h2>}
              {section.subheading && <p className="opacity-70 mb-8">{section.subheading}</p>}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {(section.items || []).map((item, j) => (
                  <div key={j} className="p-5 rounded-xl" style={{ backgroundColor: bg === '#ffffff' ? '#f8fafc' : 'rgba(255,255,255,0.05)' }}>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: (theme?.primary_color || '#10B981') + '20' }}>
                      <Zap className="h-5 w-5" style={{ color: theme?.primary_color || '#10B981' }} />
                    </div>
                    <h3 className="font-semibold mb-1">{item.title}</h3>
                    <p className="text-sm opacity-60">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 'cta':
        return (
          <div key={i} style={{ background: bg, color: txt }} className="py-14 px-6">
            <div className={cn("max-w-2xl mx-auto", align)}>
              {section.heading && <h2 className="text-2xl font-bold mb-2">{section.heading}</h2>}
              {section.subheading && <p className="opacity-80 mb-6">{section.subheading}</p>}
              {section.button_text && (
                <a href={section.button_url || '#'} className="inline-block px-8 py-3 rounded-lg bg-white text-gray-900 font-semibold text-sm hover:opacity-90 transition shadow-lg">{section.button_text}</a>
              )}
            </div>
          </div>
        );
      case 'stats':
        return (
          <div key={i} style={{ background: bg, color: txt }} className="py-12 px-6">
            <div className={cn("max-w-4xl mx-auto", align)}>
              {section.heading && <h2 className="text-2xl font-bold mb-8">{section.heading}</h2>}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {(section.items || []).map((item, j) => (
                  <div key={j}>
                    <p className="text-3xl font-bold" style={{ color: theme?.primary_color || '#10B981' }}>{item.title}</p>
                    <p className="text-sm opacity-60 mt-1">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 'testimonials':
        return (
          <div key={i} style={{ background: bg, color: txt }} className="py-12 px-6">
            <div className={cn("max-w-4xl mx-auto", align)}>
              {section.heading && <h2 className="text-2xl font-bold mb-8">{section.heading}</h2>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {(section.items || []).map((item, j) => (
                  <div key={j} className="p-5 rounded-xl border" style={{ borderColor: txt + '15', backgroundColor: bg === '#ffffff' ? '#f8fafc' : 'rgba(255,255,255,0.05)' }}>
                    <div className="flex gap-1 mb-3">{[1,2,3,4,5].map(s => <Star key={s} className="h-4 w-4 fill-amber-400 text-amber-400" />)}</div>
                    <p className="text-sm italic opacity-80 mb-3">"{item.description}"</p>
                    <p className="text-xs font-semibold">{item.title}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 'pricing':
        return (
          <div key={i} style={{ background: bg, color: txt }} className="py-12 px-6">
            <div className={cn("max-w-4xl mx-auto", align)}>
              {section.heading && <h2 className="text-2xl font-bold mb-8">{section.heading}</h2>}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {(section.items || []).map((item, j) => (
                  <div key={j} className={cn("p-6 rounded-xl border", j === 1 ? "border-2 scale-105" : "")} style={{ borderColor: j === 1 ? (theme?.primary_color || '#10B981') : txt + '15' }}>
                    <h3 className="font-bold text-lg mb-1">{item.title}</h3>
                    <p className="text-sm opacity-60 mb-4">{item.description}</p>
                    <button className="w-full py-2 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: theme?.primary_color || '#10B981' }}>Choose Plan</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 'faq':
        return (
          <div key={i} style={{ background: bg, color: txt }} className="py-12 px-6">
            <div className={cn("max-w-3xl mx-auto", align)}>
              {section.heading && <h2 className="text-2xl font-bold mb-8">{section.heading}</h2>}
              <div className="space-y-3 text-left">
                {(section.items || []).map((item, j) => (
                  <div key={j} className="p-4 rounded-lg" style={{ backgroundColor: bg === '#ffffff' ? '#f8fafc' : 'rgba(255,255,255,0.05)' }}>
                    <p className="font-semibold text-sm mb-1">{item.title}</p>
                    <p className="text-sm opacity-60">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div key={i} style={{ background: bg, color: txt }} className="py-10 px-6">
            <div className={cn("max-w-3xl mx-auto", align)}>
              {section.heading && <h2 className="text-2xl font-bold mb-2">{section.heading}</h2>}
              {section.content && <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: section.content }} />}
            </div>
          </div>
        );
    }
  };

  if (sections.length === 0 && !(rawHtml && rawHtml.trim())) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-900/50 rounded-xl border border-dashed border-slate-700">
        <div className="text-center p-8">
          <Monitor className="h-10 w-10 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Add sections, paste HTML in the Code tab, or use AI to generate content</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-inner border border-gray-200" data-testid="live-preview" style={{ fontFamily: theme?.font || 'Inter, sans-serif' }}>
      {sections.map((s, i) => renderSection(s, i))}
    </div>
  );
}


// ═══════ MAIN PAGE ═══════
export default function LandingPagesPage() {
  const [pages, setPages] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list, object = editing page
  const [sections, setSections] = useState([]);
  const [pageForm, setPageForm] = useState({ name: '', slug: '', seo_title: '', seo_description: '', status: 'draft', raw_html_override: '', custom_css: '' });
  const [theme, setTheme] = useState({ primary_color: '#10B981', bg_color: '#0f172a', text_color: '#ffffff', font: 'Inter' });
  const [saving, setSaving] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiConversation, setAiConversation] = useState([]); // [{role:'user'|'assistant', content}]
  const [aiAttachments, setAiAttachments] = useState([]); // [{name, mediaType, base64, dataUrl}]
  const aiFileInputRef = useRef(null);
  const [showAddSection, setShowAddSection] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [tab, setTab] = useState('sections');
  const [shareDialog, setShareDialog] = useState(null); // null | page
  const [analyticsDialog, setAnalyticsDialog] = useState(null); // null | page
  const [analyticsData, setAnalyticsData] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [shareTab, setShareTab] = useState('direct'); // 'direct' | 'campaigns'
  const [newCampaign, setNewCampaign] = useState({ name: '', utm_source: '', utm_medium: '', utm_campaign: '', utm_content: '', notes: '' });
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [activeCampaignId, setActiveCampaignId] = useState(null); // for QR display

  const fetchPages = useCallback(async () => {
    try {
      const [pagesRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/platform/pages`),
        axios.get(`${API}/api/platform/pages/stats`),
      ]);
      setPages(pagesRes.data || []);
      setStats(statsRes.data || {});
    } catch { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPages(); }, [fetchPages]);

  const startEditing = (page) => {
    setEditing(page);
    setSections(page.sections || []);
    setPageForm({
      name: page.name, slug: page.slug || '',
      seo_title: page.seo_title || '', seo_description: page.seo_description || '',
      status: page.status || 'draft',
      raw_html_override: page.raw_html_override || '',
      custom_css: page.custom_css || '',
    });
    setTheme(page.theme || { primary_color: '#10B981', bg_color: '#0f172a', text_color: '#ffffff', font: 'Inter' });
    setAiConversation([]);
  };

  const startNew = (template) => {
    const tpl = TEMPLATES.find(t => t.id === template) || TEMPLATES[0];
    setEditing({ id: null });
    setSections(tpl.sections ? JSON.parse(JSON.stringify(tpl.sections)) : []);
    setPageForm({ name: '', slug: '', seo_title: '', seo_description: '', status: 'draft', raw_html_override: '', custom_css: '' });
    setTheme({ primary_color: '#10B981', bg_color: '#0f172a', text_color: '#ffffff', font: 'Inter' });
    setAiConversation([]);
  };

  const handleSave = async () => {
    if (!pageForm.name.trim()) { toast.error('Page name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...pageForm, sections, theme };
      if (editing?.id) {
        const res = await axios.put(`${API}/api/platform/pages/${editing.id}`, payload);
        toast.success('Page saved');
        setEditing(res.data);
      } else {
        const res = await axios.post(`${API}/api/platform/pages`, payload);
        toast.success('Page created');
        setEditing(res.data);
      }
      fetchPages();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this page?')) return;
    try {
      await axios.delete(`${API}/api/platform/pages/${id}`);
      toast.success('Page deleted');
      fetchPages();
    } catch { toast.error('Failed to delete'); }
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim() && aiAttachments.length === 0) { toast.error('Describe what you want or attach an image'); return; }
    setAiLoading(true);
    const userMsg = aiPrompt.trim() || '(image attached)';
    const sentAttachments = [...aiAttachments];
    const isRefine = sections.length > 0;
    // Optimistically add to conversation (with image previews)
    setAiConversation(prev => [...prev, { role: 'user', content: userMsg, images: sentAttachments.map(a => a.dataUrl) }]);
    setAiPrompt('');
    setAiAttachments([]);
    try {
      const res = await axios.post(`${API}/api/platform/pages/ai-generate`, {
        prompt: userMsg,
        mode: isRefine ? 'refine' : 'replace',
        current_sections: isRefine ? sections : [],
        conversation: aiConversation,
        attachments: sentAttachments.map(a => ({ media_type: a.mediaType, data: a.base64 })),
      });
      const newSections = res.data.sections || [];
      setSections(newSections);
      const summary = isRefine
        ? `Updated the page — ${newSections.length} sections now.`
        : `Generated ${newSections.length} sections from scratch.`;
      setAiConversation(prev => [...prev, { role: 'assistant', content: summary }]);
      toast.success(summary);
    } catch (err) {
      const detail = err.response?.data?.detail || 'AI failed';
      toast.error(detail);
      setAiConversation(prev => [...prev, { role: 'assistant', content: `Error: ${detail}` }]);
    }
    setAiLoading(false);
  };

  const resetAiChat = () => {
    setAiConversation([]); setAiAttachments([]);
    toast.info('AI chat reset — next prompt will start fresh');
  };

  const handleAiFilesSelected = async (filesList) => {
    const files = Array.from(filesList || []).filter(f => f.type.startsWith('image/'));
    if (files.length === 0) return;
    const remaining = Math.max(0, 4 - aiAttachments.length);
    if (remaining === 0) { toast.error('Max 4 images per turn'); return; }
    const next = [];
    for (const file of files.slice(0, remaining)) {
      if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} too large (max 5MB)`); continue; }
      const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = e => res(e.target.result); r.readAsDataURL(file); });
      const base64 = dataUrl.split(',')[1] || '';
      next.push({ name: file.name, mediaType: file.type, base64, dataUrl });
    }
    if (next.length) setAiAttachments(prev => [...prev, ...next]);
  };

  const removeAiAttachment = (idx) => setAiAttachments(prev => prev.filter((_, i) => i !== idx));

  const openShareDialog = async (page) => {
    try {
      const { data } = await axios.get(`${API}/api/platform/pages/${page.id}/share-url`);
      setShareDialog({ page, share: data });
      setShareTab('direct');
      setActiveCampaignId(null);
      // Pre-fill campaign defaults — use page slug as the campaign tag
      setNewCampaign({ name: '', utm_source: '', utm_medium: '', utm_campaign: page.slug || '', utm_content: '', notes: '' });
      // Load campaigns in parallel (non-blocking)
      setCampaignsLoading(true);
      try {
        const campRes = await axios.get(`${API}/api/platform/pages/${page.id}/campaigns`);
        setCampaigns(campRes.data || []);
      } catch { setCampaigns([]); }
      setCampaignsLoading(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not build share URL');
    }
  };

  const buildCampaignUrl = (basePath, c) => {
    const fullBase = (typeof window !== 'undefined' ? window.location.origin : '') + basePath;
    const qs = new URLSearchParams();
    if (c.utm_source) qs.set('utm_source', c.utm_source);
    if (c.utm_medium) qs.set('utm_medium', c.utm_medium);
    if (c.utm_campaign) qs.set('utm_campaign', c.utm_campaign);
    if (c.utm_content) qs.set('utm_content', c.utm_content);
    if (c.utm_term) qs.set('utm_term', c.utm_term);
    const q = qs.toString();
    return q ? `${fullBase}?${q}` : fullBase;
  };

  const handleCreateCampaign = async () => {
    if (!shareDialog) return;
    if (!newCampaign.name.trim() || !newCampaign.utm_source.trim()) {
      toast.error('Campaign name and source are required');
      return;
    }
    setSavingCampaign(true);
    try {
      const { data } = await axios.post(`${API}/api/platform/pages/${shareDialog.page.id}/campaigns`, newCampaign);
      setCampaigns(prev => [data, ...prev]);
      setActiveCampaignId(data.id);
      setNewCampaign({ name: '', utm_source: '', utm_medium: '', utm_campaign: shareDialog.share.page_slug || '', utm_content: '', notes: '' });
      toast.success('Campaign created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create campaign');
    }
    setSavingCampaign(false);
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!shareDialog) return;
    if (!window.confirm('Delete this campaign? View history is kept.')) return;
    try {
      await axios.delete(`${API}/api/platform/pages/${shareDialog.page.id}/campaigns/${campaignId}`);
      setCampaigns(prev => prev.filter(c => c.id !== campaignId));
      if (activeCampaignId === campaignId) setActiveCampaignId(null);
      toast.success('Campaign deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const downloadCurrentQR = (filenameSuffix) => {
    const svg = document.querySelector('[data-testid="share-qr"]');
    if (!svg) return;
    const serializer = new XMLSerializer();
    const src = serializer.serializeToString(svg);
    const blob = new Blob([src], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `qr-${filenameSuffix || 'page'}.svg`; a.click();
    URL.revokeObjectURL(url);
  };

  const openAnalyticsDialog = async (page) => {
    setAnalyticsDialog(page);
    setAnalyticsData(null);
    setFunnelData(null);
    try {
      const [analyticsRes, funnelRes] = await Promise.all([
        axios.get(`${API}/api/platform/pages/${page.id}/analytics?days=30`),
        axios.get(`${API}/api/platform/pages/${page.id}/funnel?days=30`),
      ]);
      setAnalyticsData(analyticsRes.data);
      setFunnelData(funnelRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analytics fetch failed');
      setAnalyticsData({ error: true });
    }
  };

  // Section management
  const updateSection = (i, section) => setSections(s => s.map((sec, j) => j === i ? section : sec));
  const removeSection = (i) => setSections(s => s.filter((_, j) => j !== i));
  const moveUp = (i) => { if (i === 0) return; setSections(s => { const n = [...s]; [n[i-1], n[i]] = [n[i], n[i-1]]; return n; }); };
  const moveDown = (i) => { setSections(s => { if (i >= s.length - 1) return s; const n = [...s]; [n[i], n[i+1]] = [n[i+1], n[i]]; return n; }); };
  const addSection = (type) => { setSections(s => [...s, DEFAULT_SECTION(type)]); setShowAddSection(false); };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 text-emerald-500 animate-spin" /></div>;
  }

  // ─── EDITOR VIEW ───
  if (editing) {
    return (
      <div className="h-[calc(100vh-80px)] flex flex-col" data-testid="page-editor">
        {/* Editor Toolbar */}
        <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="gap-1.5 text-xs" onClick={() => setEditing(null)} data-testid="back-to-list">
              <ArrowLeft className="h-3.5 w-3.5" />Back
            </Button>
            <Input className="h-8 text-sm font-semibold w-64 border-none bg-gray-50 px-2" placeholder="Page name..."
              value={pageForm.name} onChange={e => setPageForm(f => ({ ...f, name: e.target.value }))} data-testid="page-name-input" />
            <Badge className={cn("text-[9px]", pageForm.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700')}>
              {pageForm.status}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5 gap-0.5">
              <button onClick={() => setPreviewMode('desktop')} className={cn("p-1.5 rounded", previewMode === 'desktop' ? "bg-white shadow-sm" : "")}>
                <Monitor className="h-3.5 w-3.5 text-gray-600" />
              </button>
              <button onClick={() => setPreviewMode('mobile')} className={cn("p-1.5 rounded", previewMode === 'mobile' ? "bg-white shadow-sm" : "")}>
                <Smartphone className="h-3.5 w-3.5 text-gray-600" />
              </button>
            </div>
            <Select value={pageForm.status} onValueChange={v => setPageForm(f => ({ ...f, status: v }))}>
              <SelectTrigger className="h-8 w-28 text-xs" data-testid="page-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 gap-1.5 text-xs" onClick={handleSave} disabled={saving} data-testid="save-page-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}Save
            </Button>
          </div>
        </div>

        {/* Editor Body */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel: Section Editor */}
          <div className="w-[420px] border-r border-gray-200 flex flex-col bg-gray-50 shrink-0 overflow-hidden">
            <Tabs value={tab} onValueChange={setTab} className="flex flex-col flex-1 overflow-hidden">
              <TabsList className="mx-3 mt-2 bg-white shrink-0">
                <TabsTrigger value="sections" className="text-[10px] gap-1"><Layout className="h-3 w-3" />Sections</TabsTrigger>
                <TabsTrigger value="ai" className="text-[10px] gap-1"><Sparkles className="h-3 w-3" />AI Generate</TabsTrigger>
                <TabsTrigger value="code" className="text-[10px] gap-1"><Code className="h-3 w-3" />Code</TabsTrigger>
                <TabsTrigger value="theme" className="text-[10px] gap-1"><Palette className="h-3 w-3" />Theme</TabsTrigger>
                <TabsTrigger value="seo" className="text-[10px] gap-1"><Globe className="h-3 w-3" />SEO</TabsTrigger>
              </TabsList>

              <TabsContent value="sections" className="flex-1 overflow-y-auto px-3 pb-3 mt-0">
                <div className="space-y-2">
                  {sections.map((s, i) => (
                    <SectionEditor key={i} section={s} index={i} onUpdate={updateSection} onRemove={removeSection}
                      onMoveUp={moveUp} onMoveDown={moveDown} isFirst={i === 0} isLast={i === sections.length - 1} />
                  ))}
                </div>
                <Button variant="outline" className="w-full mt-3 gap-2 text-xs border-dashed border-gray-300 text-gray-500 hover:text-emerald-600 hover:border-emerald-400"
                  onClick={() => setShowAddSection(true)} data-testid="add-section-btn">
                  <Plus className="h-3.5 w-3.5" />Add Section
                </Button>
              </TabsContent>

              <TabsContent value="ai" className="flex-1 overflow-y-auto px-3 pb-3 mt-0">
                <Card className="bg-white border-gray-200">
                  <CardContent className="p-3 space-y-3">
                    <div className="p-2.5 bg-emerald-50 rounded-lg border border-emerald-200 flex items-start gap-2">
                      <Sparkles className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-emerald-700 font-medium">AI Page Builder (Chat)</p>
                        <p className="text-[10px] text-emerald-600 mt-0.5 leading-snug">
                          {sections.length === 0
                            ? 'Describe the page you want to create.'
                            : 'Ask for refinements — each message builds on the previous one. E.g. "Change the hero color to navy", "Add a pricing section", "Rewrite the headline to be more punchy".'}
                        </p>
                      </div>
                    </div>

                    {/* Conversation history */}
                    {aiConversation.length > 0 && (
                      <div className="bg-gray-50 rounded-lg border border-gray-200 max-h-[280px] overflow-y-auto p-2 space-y-2" data-testid="ai-conversation">
                        {aiConversation.map((m, idx) => (
                          <div key={idx} className={cn("flex gap-1.5", m.role === 'user' ? "justify-end" : "justify-start")}>
                            <div className={cn(
                              "rounded-lg px-2.5 py-1.5 text-[11px] leading-snug max-w-[85%]",
                              m.role === 'user' ? "bg-emerald-600 text-white" : "bg-white border border-gray-200 text-gray-700"
                            )}>
                              {m.images && m.images.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-1">
                                  {m.images.map((src, ii) => (
                                    <img key={ii} src={src} alt="" className="h-12 w-12 rounded object-cover border border-white/30" />
                                  ))}
                                </div>
                              )}
                              {m.content}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Pending attachment previews */}
                    {aiAttachments.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 p-2 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="ai-attachments">
                        {aiAttachments.map((a, idx) => (
                          <div key={idx} className="relative group">
                            <img src={a.dataUrl} alt={a.name} className="h-14 w-14 rounded border border-emerald-300 object-cover" />
                            <button
                              type="button"
                              onClick={() => removeAiAttachment(idx)}
                              className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-white flex items-center justify-center opacity-90 hover:opacity-100"
                              data-testid={`remove-ai-attachment-${idx}`}
                              title="Remove"
                            >
                              <X className="h-2.5 w-2.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <input
                      ref={aiFileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => { handleAiFilesSelected(e.target.files); e.target.value = ''; }}
                      data-testid="ai-file-input"
                    />

                    <Textarea
                      className="min-h-[90px] text-xs"
                      placeholder={sections.length === 0
                        ? "E.g. A landing page for a SaaS CRM for small businesses — hero, feature grid, pricing, FAQ. Attach a logo or moodboard for stronger results."
                        : "E.g. Make the hero background navy blue and add a social proof section with 3 testimonials."}
                      value={aiPrompt}
                      onChange={e => setAiPrompt(e.target.value)}
                      onPaste={(e) => {
                        const items = (e.clipboardData || {}).items || [];
                        const imgs = [];
                        for (const it of items) { if (it.type && it.type.startsWith('image/')) { const f = it.getAsFile(); if (f) imgs.push(f); } }
                        if (imgs.length) { e.preventDefault(); handleAiFilesSelected(imgs); }
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleAiGenerate(); }
                      }}
                      data-testid="ai-prompt-input"
                    />

                    <div className="flex gap-1.5">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-xs gap-1.5 h-auto"
                        onClick={() => aiFileInputRef.current && aiFileInputRef.current.click()}
                        disabled={aiLoading || aiAttachments.length >= 4}
                        data-testid="ai-attach-btn"
                        title={aiAttachments.length >= 4 ? 'Max 4 images' : 'Attach image (logo, moodboard, screenshot)'}
                      >
                        <Paperclip className="h-3.5 w-3.5" />
                        {aiAttachments.length > 0 ? `${aiAttachments.length}/4` : ''}
                      </Button>
                      <Button
                        className="bg-emerald-600 hover:bg-emerald-500 gap-2 text-xs flex-1"
                        onClick={handleAiGenerate}
                        disabled={aiLoading || (!aiPrompt.trim() && aiAttachments.length === 0)}
                        data-testid="ai-generate-btn"
                      >
                        {aiLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                        {aiLoading ? 'Thinking…' : (sections.length === 0 ? 'Generate' : 'Refine')}
                      </Button>
                      {aiConversation.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs gap-1.5 h-auto"
                          onClick={resetAiChat}
                          data-testid="ai-reset-btn"
                          title="Reset conversation (next prompt starts fresh)"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>

                    <p className="text-[10px] text-gray-400 text-center">
                      ⌘/Ctrl + Enter to send · Paste or attach images for visual context · {sections.length > 0 ? 'Refine mode preserves existing sections' : 'Fresh generation'}
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="code" className="flex-1 overflow-y-auto px-3 pb-3 mt-0">
                <Card className="bg-white border-gray-200">
                  <CardContent className="p-3 space-y-3">
                    <div className="p-2.5 bg-violet-50 rounded-lg border border-violet-200">
                      <p className="text-xs text-violet-700 font-medium flex items-center gap-1.5"><Code className="h-3.5 w-3.5" />Full HTML Override</p>
                      <p className="text-[10px] text-violet-600 mt-0.5 leading-snug">
                        Paste a complete HTML page below — when set, it <b>replaces all sections</b>. Add <code className="bg-white px-1 rounded text-[9px]">data-konnect-form="1"</code> to any <code className="bg-white px-1 rounded text-[9px]">&lt;form&gt;</code> to auto-capture leads to your CRM.
                      </p>
                    </div>
                    <div>
                      <Label className="text-[10px] text-gray-500">HTML override (leaves empty to use sections)</Label>
                      <Textarea
                        className="text-[11px] mt-1 min-h-[260px] font-mono"
                        placeholder={'<!doctype html><html><head><style>body{font-family:sans-serif}</style></head><body>\n  <section style="padding:60px;text-align:center"><h1>Your custom page</h1>\n    <form data-konnect-form="1"><input name="email" type="email" required/><button>Sign up</button></form>\n  </section>\n</body></html>'}
                        value={pageForm.raw_html_override || ''}
                        onChange={e => setPageForm(f => ({ ...f, raw_html_override: e.target.value }))}
                        data-testid="raw-html-input"
                      />
                      {pageForm.raw_html_override && (
                        <div className="flex items-center gap-2 mt-1.5">
                          <Badge className="bg-violet-100 text-violet-700 text-[9px]">HTML override active</Badge>
                          <Button variant="ghost" size="sm" className="h-6 text-[10px] text-red-500 gap-1"
                            onClick={() => setPageForm(f => ({ ...f, raw_html_override: '' }))} data-testid="clear-html-btn">
                            <Trash2 className="h-3 w-3" />Clear & use sections
                          </Button>
                        </div>
                      )}
                    </div>
                    <div>
                      <Label className="text-[10px] text-gray-500">Custom CSS (always applied)</Label>
                      <Textarea
                        className="text-[11px] mt-1 min-h-[100px] font-mono"
                        placeholder=".my-class { color: red; }"
                        value={pageForm.custom_css || ''}
                        onChange={e => setPageForm(f => ({ ...f, custom_css: e.target.value }))}
                        data-testid="custom-css-input"
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>


              <TabsContent value="theme" className="flex-1 overflow-y-auto px-3 pb-3 mt-0">
                <Card className="bg-white border-gray-200">
                  <CardContent className="p-3 space-y-3">
                    <div>
                      <Label className="text-[10px] text-gray-500">Primary Color</Label>
                      <div className="flex gap-2 mt-1">
                        <input type="color" value={theme.primary_color} onChange={e => setTheme(t => ({ ...t, primary_color: e.target.value }))} className="w-9 h-9 rounded border cursor-pointer" />
                        <Input className="h-9 text-xs flex-1" value={theme.primary_color} onChange={e => setTheme(t => ({ ...t, primary_color: e.target.value }))} />
                      </div>
                    </div>
                    <div>
                      <Label className="text-[10px] text-gray-500">Font Family</Label>
                      <Select value={theme.font || 'Inter'} onValueChange={v => setTheme(t => ({ ...t, font: v }))}>
                        <SelectTrigger className="h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['Inter', 'Poppins', 'DM Sans', 'Space Grotesk', 'Plus Jakarta Sans', 'Manrope', 'Outfit'].map(f => (
                            <SelectItem key={f} value={f}>{f}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { color: '#10B981', name: 'Emerald' },
                        { color: '#3B82F6', name: 'Blue' },
                        { color: '#8B5CF6', name: 'Purple' },
                        { color: '#F59E0B', name: 'Amber' },
                        { color: '#EF4444', name: 'Red' },
                        { color: '#EC4899', name: 'Pink' },
                      ].map(c => (
                        <button key={c.color} onClick={() => setTheme(t => ({ ...t, primary_color: c.color }))}
                          className={cn("p-2 rounded-lg border text-center transition-all",
                            theme.primary_color === c.color ? "border-gray-400 shadow-sm" : "border-gray-200 hover:border-gray-300"
                          )}>
                          <div className="w-6 h-6 rounded-full mx-auto mb-1" style={{ backgroundColor: c.color }} />
                          <p className="text-[9px] text-gray-600">{c.name}</p>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="seo" className="flex-1 overflow-y-auto px-3 pb-3 mt-0">
                <Card className="bg-white border-gray-200">
                  <CardContent className="p-3 space-y-3">
                    <div>
                      <Label className="text-[10px] text-gray-500">URL Slug</Label>
                      <Input className="h-8 text-xs mt-1" placeholder="my-landing-page" value={pageForm.slug}
                        onChange={e => setPageForm(f => ({ ...f, slug: e.target.value }))} data-testid="page-slug" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-gray-500">SEO Title</Label>
                      <Input className="h-8 text-xs mt-1" placeholder="Page title for search engines" value={pageForm.seo_title}
                        onChange={e => setPageForm(f => ({ ...f, seo_title: e.target.value }))} data-testid="seo-title" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-gray-500">SEO Description</Label>
                      <Textarea className="text-xs mt-1 min-h-[80px]" placeholder="Meta description for search results" value={pageForm.seo_description}
                        onChange={e => setPageForm(f => ({ ...f, seo_description: e.target.value }))} data-testid="seo-desc" />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Panel: Live Preview */}
          <div className="flex-1 bg-gray-100 p-4 overflow-y-auto">
            <div className={cn("mx-auto transition-all", previewMode === 'mobile' ? 'max-w-sm' : 'max-w-full')}>
              <LivePreview sections={sections} theme={theme} rawHtml={pageForm.raw_html_override} />
            </div>
          </div>
        </div>

        {/* Add Section Dialog */}
        <Dialog open={showAddSection} onOpenChange={setShowAddSection}>
          <DialogContent className="max-w-md" data-testid="add-section-dialog">
            <DialogHeader><DialogTitle>Add Section</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-2 max-h-[400px] overflow-y-auto">
              {SECTION_TYPES.map(st => (
                <button key={st.type} onClick={() => addSection(st.type)}
                  className="flex items-start gap-2.5 p-3 rounded-xl border border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all text-left"
                  data-testid={`add-${st.type}`}>
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                    <st.icon className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-800">{st.label}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">{st.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ─── LIST VIEW ───
  return (
    <div className="space-y-5 animate-fadeIn" data-testid="landing-pages-list">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Landing Page Builder<PageInfoTooltip pageKey="landing-pages" /></h1>
          <p className="text-sm text-gray-medium">Create high-converting pages with AI-powered design</p>
        </div>
        <Button className="bg-emerald-600 hover:bg-emerald-500 gap-2 text-xs" onClick={() => startNew('blank')} data-testid="new-page-btn">
          <Plus className="h-4 w-4" />New Page
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-white border-gray-border"><CardContent className="p-4">
          <p className="text-2xl font-bold text-text-primary">{stats.total_pages || 0}</p>
          <p className="text-xs text-gray-medium">Total Pages</p>
        </CardContent></Card>
        <Card className="bg-white border-gray-border"><CardContent className="p-4">
          <p className="text-2xl font-bold text-emerald-600">{stats.published || 0}</p>
          <p className="text-xs text-gray-medium">Published</p>
        </CardContent></Card>
        <Card className="bg-white border-gray-border"><CardContent className="p-4">
          <p className="text-2xl font-bold text-blue-600">{(stats.total_views || 0).toLocaleString()}</p>
          <p className="text-xs text-gray-medium">Total Views</p>
        </CardContent></Card>
      </div>

      {/* Template Gallery */}
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-3">Start from a Template</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {TEMPLATES.map(tpl => (
            <Card key={tpl.id} className="bg-white border-gray-200 hover:border-emerald-400 hover:shadow-sm cursor-pointer transition-all group"
              onClick={() => startNew(tpl.id)} data-testid={`template-${tpl.id}`}>
              <CardContent className="p-4">
                <div className="w-full h-20 bg-gradient-to-br from-gray-100 to-gray-50 rounded-lg mb-3 flex items-center justify-center group-hover:from-emerald-50 group-hover:to-emerald-100 transition-colors">
                  <Layout className="h-8 w-8 text-gray-300 group-hover:text-emerald-400 transition-colors" />
                </div>
                <p className="text-sm font-semibold text-gray-800">{tpl.name}</p>
                <p className="text-[10px] text-gray-500 mt-0.5">{tpl.desc}</p>
                <Badge className="mt-2 text-[9px] bg-gray-100 text-gray-500">{tpl.sections.length} sections</Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Existing Pages */}
      {pages.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-3">Your Pages</p>
          <div className="grid gap-2">
            {pages.map(p => (
              <Card key={p.id} className="bg-white border-gray-200 hover:shadow-sm transition-shadow" data-testid={`page-card-${p.id}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                    <FileText className="h-5 w-5 text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">{p.name}</p>
                      <Badge className={cn("text-[9px]", p.status === 'published' ? 'bg-emerald-100 text-emerald-700' : p.status === 'archived' ? 'bg-gray-100 text-gray-500' : 'bg-amber-100 text-amber-700')}>
                        {p.status}
                      </Badge>
                    </div>
                    <p className="text-[10px] text-gray-400">/{p.slug || '—'} · {(p.sections || []).length} sections · {p.views || 0} views</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1" onClick={() => openShareDialog(p)} data-testid={`share-page-${p.id}`} title={p.status === 'published' ? 'Share / QR code' : 'Publish to enable sharing'}>
                      <Share2 className="h-3 w-3" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1" onClick={() => openAnalyticsDialog(p)} data-testid={`analytics-page-${p.id}`} title="View analytics">
                      <BarChart2 className="h-3 w-3" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1" onClick={() => startEditing(p)} data-testid={`edit-page-${p.id}`}>
                      <PenLine className="h-3 w-3" />Edit
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-red-500 hover:text-red-600" onClick={() => handleDelete(p.id)} data-testid={`delete-page-${p.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Share Dialog */}
      <Dialog open={!!shareDialog} onOpenChange={(o) => !o && setShareDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="share-dialog">
          <DialogHeader><DialogTitle>Share & promote — {shareDialog?.page?.name}</DialogTitle></DialogHeader>
          {shareDialog && (() => {
            const directUrl = (typeof window !== 'undefined' ? window.location.origin : '') + shareDialog.share.path;
            const isPub = shareDialog.share.is_published;
            const activeCamp = campaigns.find(c => c.id === activeCampaignId);
            const qrUrl = activeCamp ? buildCampaignUrl(shareDialog.share.path, activeCamp) : directUrl;
            return (
              <div className="space-y-4" data-testid="share-content">
                {!isPub && (
                  <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                    This page is still <b>Draft</b>. Change status to <b>Published</b> and save before sharing — visitors will see a 404 otherwise.
                  </div>
                )}

                {/* Tabs */}
                <div className="flex border-b border-gray-200">
                  <button
                    type="button"
                    onClick={() => { setShareTab('direct'); setActiveCampaignId(null); }}
                    className={cn("px-4 py-2 text-xs font-medium border-b-2 transition-colors",
                      shareTab === 'direct' ? "border-emerald-500 text-emerald-600" : "border-transparent text-gray-500 hover:text-gray-700")}
                    data-testid="share-tab-direct"
                  >
                    Direct link
                  </button>
                  <button
                    type="button"
                    onClick={() => setShareTab('campaigns')}
                    className={cn("px-4 py-2 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5",
                      shareTab === 'campaigns' ? "border-emerald-500 text-emerald-600" : "border-transparent text-gray-500 hover:text-gray-700")}
                    data-testid="share-tab-campaigns"
                  >
                    Campaigns
                    {campaigns.length > 0 && <Badge className="bg-emerald-100 text-emerald-700 text-[9px] h-4 px-1.5">{campaigns.length}</Badge>}
                  </button>
                </div>

                {/* Two-column: left = form/list, right = QR */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  {/* LEFT */}
                  <div className="md:col-span-3 space-y-3">
                    {shareTab === 'direct' ? (
                      <>
                        <div>
                          <Label className="text-[10px] text-gray-500">Public URL</Label>
                          <div className="flex gap-1.5 mt-1">
                            <Input className="h-9 text-xs font-mono" value={directUrl} readOnly data-testid="share-url-input" onFocus={(e) => e.target.select()} />
                            <Button size="sm" variant="outline" className="h-9 gap-1.5 text-xs"
                              onClick={() => { navigator.clipboard.writeText(directUrl); toast.success('Copied'); }}
                              data-testid="copy-share-url-btn"><Copy className="h-3.5 w-3.5" />Copy</Button>
                          </div>
                          <p className="text-[10px] text-gray-400 mt-1">Use this for organic traffic — analytics will count these as <b>Direct</b>.</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="flex-1 gap-1.5 text-xs" onClick={() => window.open(directUrl, '_blank')} data-testid="open-public-btn">
                            <ExternalLink className="h-3.5 w-3.5" />Open
                          </Button>
                          <Button size="sm" variant="outline" className="flex-1 gap-1.5 text-xs"
                            onClick={() => downloadCurrentQR(shareDialog.page.slug)} data-testid="download-qr-btn">
                            <QrCode className="h-3.5 w-3.5" />Download QR
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Existing campaigns list */}
                        {campaignsLoading ? (
                          <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-emerald-500" /></div>
                        ) : campaigns.length === 0 ? (
                          <p className="text-xs text-gray-500 py-2">No campaigns yet. Create one below to get a UTM-tagged URL + QR for each channel.</p>
                        ) : (
                          <div className="space-y-1.5 max-h-44 overflow-y-auto" data-testid="campaign-list">
                            {campaigns.map(c => {
                              const url = buildCampaignUrl(shareDialog.share.path, c);
                              const active = c.id === activeCampaignId;
                              return (
                                <div key={c.id} className={cn("rounded-lg border p-2.5 transition-all cursor-pointer",
                                  active ? "border-emerald-400 bg-emerald-50" : "border-gray-200 hover:border-gray-300 bg-white")}
                                  onClick={() => setActiveCampaignId(c.id)} data-testid={`campaign-${c.id}`}>
                                  <div className="flex items-start gap-2">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-1.5">
                                        <span className="text-xs font-semibold text-gray-800 truncate">{c.name}</span>
                                        <Badge className="bg-gray-100 text-gray-600 text-[9px]">{c.utm_source}</Badge>
                                        {c.utm_medium && <Badge className="bg-gray-100 text-gray-600 text-[9px]">{c.utm_medium}</Badge>}
                                        <span className="ml-auto text-[10px] text-emerald-600 font-medium">{c.click_count || 0} clicks</span>
                                      </div>
                                      <div className="flex gap-1 mt-1">
                                        <Input className="h-6 text-[10px] font-mono" value={url} readOnly onFocus={(e) => e.target.select()} onClick={(e) => e.stopPropagation()} />
                                        <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(url); toast.success('Copied'); }}
                                          className="p-1 hover:bg-gray-100 rounded" data-testid={`copy-camp-${c.id}`} title="Copy">
                                          <Copy className="h-3 w-3 text-gray-500" />
                                        </button>
                                        <button onClick={(e) => { e.stopPropagation(); window.open(url, '_blank'); }}
                                          className="p-1 hover:bg-gray-100 rounded" title="Open"><ExternalLink className="h-3 w-3 text-gray-500" /></button>
                                        <button onClick={(e) => { e.stopPropagation(); handleDeleteCampaign(c.id); }}
                                          className="p-1 hover:bg-red-100 rounded" data-testid={`delete-camp-${c.id}`} title="Delete">
                                          <Trash2 className="h-3 w-3 text-red-400" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* New campaign form */}
                        <Card className="bg-gray-50 border-gray-200">
                          <CardContent className="p-3 space-y-2">
                            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">New campaign</p>
                            {/* Channel preset chips */}
                            <div className="flex flex-wrap gap-1">
                              {[
                                { label: 'Facebook', source: 'facebook', medium: 'social' },
                                { label: 'Instagram', source: 'instagram', medium: 'social' },
                                { label: 'TikTok', source: 'tiktok', medium: 'social' },
                                { label: 'LinkedIn', source: 'linkedin', medium: 'social' },
                                { label: 'X / Twitter', source: 'twitter', medium: 'social' },
                                { label: 'Google Ads', source: 'google', medium: 'cpc' },
                                { label: 'Email', source: 'email', medium: 'email' },
                                { label: 'WhatsApp', source: 'whatsapp', medium: 'messaging' },
                                { label: 'QR Print', source: 'qr', medium: 'offline' },
                              ].map(p => (
                                <button key={p.source} type="button"
                                  onClick={() => setNewCampaign(c => ({ ...c, utm_source: p.source, utm_medium: p.medium, name: c.name || p.label }))}
                                  className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-gray-200 hover:border-emerald-400 hover:text-emerald-600"
                                  data-testid={`preset-${p.source}`}
                                >{p.label}</button>
                              ))}
                            </div>
                            <Input className="h-7 text-[11px]" placeholder="Campaign name (e.g. Spring Sale FB)" value={newCampaign.name}
                              onChange={e => setNewCampaign(c => ({ ...c, name: e.target.value }))} data-testid="camp-name" />
                            <div className="grid grid-cols-2 gap-1.5">
                              <Input className="h-7 text-[11px]" placeholder="utm_source *" value={newCampaign.utm_source}
                                onChange={e => setNewCampaign(c => ({ ...c, utm_source: e.target.value }))} data-testid="camp-source" />
                              <Input className="h-7 text-[11px]" placeholder="utm_medium" value={newCampaign.utm_medium}
                                onChange={e => setNewCampaign(c => ({ ...c, utm_medium: e.target.value }))} data-testid="camp-medium" />
                              <Input className="h-7 text-[11px]" placeholder="utm_campaign" value={newCampaign.utm_campaign}
                                onChange={e => setNewCampaign(c => ({ ...c, utm_campaign: e.target.value }))} data-testid="camp-campaign" />
                              <Input className="h-7 text-[11px]" placeholder="utm_content (variant)" value={newCampaign.utm_content}
                                onChange={e => setNewCampaign(c => ({ ...c, utm_content: e.target.value }))} data-testid="camp-content" />
                            </div>
                            <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-500 h-8 text-xs gap-1.5"
                              onClick={handleCreateCampaign} disabled={savingCampaign} data-testid="create-campaign-btn">
                              {savingCampaign ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                              Create campaign
                            </Button>
                          </CardContent>
                        </Card>
                      </>
                    )}
                  </div>

                  {/* RIGHT: QR display */}
                  <div className="md:col-span-2 flex flex-col items-center gap-2">
                    <div className="p-3 bg-white border-2 border-gray-200 rounded-xl">
                      <QRCodeSVG value={qrUrl} size={170} level="M" data-testid="share-qr" />
                    </div>
                    <p className="text-[10px] text-gray-500 text-center">
                      {activeCamp ? <>QR for <b>{activeCamp.name}</b></> : 'QR for direct URL'}
                    </p>
                    <p className="text-[9px] font-mono text-gray-400 text-center break-all px-2">{qrUrl}</p>
                    <Button size="sm" variant="outline" className="gap-1.5 text-xs h-7 w-full"
                      onClick={() => downloadCurrentQR(activeCamp ? `${shareDialog.page.slug}-${activeCamp.utm_source}` : shareDialog.page.slug)}
                      data-testid="download-active-qr">
                      <QrCode className="h-3 w-3" />Download QR
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1.5 text-xs h-7 w-full"
                      onClick={() => { navigator.clipboard.writeText(qrUrl); toast.success('Copied'); }}>
                      <Copy className="h-3 w-3" />Copy URL
                    </Button>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={!!analyticsDialog} onOpenChange={(o) => !o && setAnalyticsDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="analytics-dialog">
          <DialogHeader><DialogTitle>Analytics — {analyticsDialog?.name}</DialogTitle></DialogHeader>
          {!analyticsData ? (
            <div className="py-10 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>
          ) : analyticsData.error ? (
            <p className="text-sm text-red-500 py-8 text-center">Could not load analytics</p>
          ) : (
            <div className="space-y-4" data-testid="analytics-content">
              {/* KPIs */}
              <div className="grid grid-cols-3 gap-2">
                <Card><CardContent className="p-3">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Total Views</p>
                  <p className="text-2xl font-bold text-emerald-600 mt-1">{(analyticsData.total_views || 0).toLocaleString()}</p>
                </CardContent></Card>
                <Card><CardContent className="p-3">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Last {analyticsData.days} days</p>
                  <p className="text-2xl font-bold text-blue-600 mt-1">{(analyticsData.recent_views || 0).toLocaleString()}</p>
                </CardContent></Card>
                <Card><CardContent className="p-3">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Unique Visitors</p>
                  <p className="text-2xl font-bold text-violet-600 mt-1">{(analyticsData.unique_visitors || 0).toLocaleString()}</p>
                </CardContent></Card>
              </div>

              {/* Timeline (simple bars) */}
              {analyticsData.timeline?.length > 0 && (
                <Card><CardContent className="p-3">
                  <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5" />Daily views</p>
                  <div className="flex items-end gap-1 h-24 pt-1">
                    {(() => {
                      const max = Math.max(...analyticsData.timeline.map(t => t.views), 1);
                      return analyticsData.timeline.map((d) => (
                        <div key={d.date} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${d.date}: ${d.views} views`}>
                          <div className="w-full bg-emerald-500 rounded-t hover:bg-emerald-600 transition-colors" style={{ height: `${(d.views / max) * 100}%`, minHeight: d.views > 0 ? 2 : 0 }} />
                        </div>
                      ));
                    })()}
                  </div>
                  <div className="flex justify-between text-[9px] text-gray-400 mt-1 font-mono">
                    <span>{analyticsData.timeline[0]?.date}</span>
                    <span>{analyticsData.timeline[analyticsData.timeline.length - 1]?.date}</span>
                  </div>
                </CardContent></Card>
              )}

              {/* Device + Referrers */}
              <div className="grid grid-cols-2 gap-3">
                <Card><CardContent className="p-3">
                  <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5"><Smartphone className="h-3.5 w-3.5" />Devices</p>
                  <div className="space-y-1.5">
                    {Object.entries(analyticsData.device_breakdown || {}).map(([k, v]) => {
                      const total = Object.values(analyticsData.device_breakdown).reduce((a, b) => a + b, 0) || 1;
                      const pct = Math.round((v / total) * 100);
                      return (
                        <div key={k} className="text-xs">
                          <div className="flex justify-between mb-0.5"><span className="capitalize">{k}</span><span className="text-gray-500">{v} · {pct}%</span></div>
                          <div className="h-1.5 bg-gray-100 rounded overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} /></div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent></Card>
                <Card><CardContent className="p-3">
                  <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5"><Globe2 className="h-3.5 w-3.5" />Top referrers</p>
                  {analyticsData.top_referrers?.length ? (
                    <div className="space-y-1 text-xs">
                      {analyticsData.top_referrers.map((r, idx) => (
                        <div key={idx} className="flex justify-between items-center"><span className="truncate flex-1">{r.referrer}</span><Badge className="bg-gray-100 text-gray-700 text-[10px]">{r.count}</Badge></div>
                      ))}
                    </div>
                  ) : <p className="text-[11px] text-gray-400 italic">No referrer data yet</p>}
                </CardContent></Card>
              </div>

              {/* Campaign / Source / Medium breakdowns */}
              {(analyticsData.by_source?.length > 0 || analyticsData.by_campaign?.length > 0) && (
                <Card><CardContent className="p-3">
                  <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5 text-emerald-500" />Campaign performance
                    <span className="ml-auto text-[10px] text-gray-400 font-normal">{analyticsData.direct_views || 0} direct</span>
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* By Source */}
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">By Source</p>
                      {analyticsData.by_source?.length ? (
                        <div className="space-y-1 text-xs">
                          {analyticsData.by_source.map((s, idx) => (
                            <div key={idx} className="flex justify-between items-center">
                              <span className="truncate flex-1 capitalize">{s.source}</span>
                              <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{s.count}</Badge>
                            </div>
                          ))}
                        </div>
                      ) : <p className="text-[10px] text-gray-400 italic">—</p>}
                    </div>
                    {/* By Medium */}
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">By Medium</p>
                      {analyticsData.by_medium?.length ? (
                        <div className="space-y-1 text-xs">
                          {analyticsData.by_medium.map((m, idx) => (
                            <div key={idx} className="flex justify-between items-center">
                              <span className="truncate flex-1 capitalize">{m.medium}</span>
                              <Badge className="bg-blue-100 text-blue-700 text-[10px]">{m.count}</Badge>
                            </div>
                          ))}
                        </div>
                      ) : <p className="text-[10px] text-gray-400 italic">—</p>}
                    </div>
                    {/* By Campaign */}
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">By Campaign</p>
                      {analyticsData.by_campaign?.length ? (
                        <div className="space-y-1 text-xs">
                          {analyticsData.by_campaign.map((c, idx) => (
                            <div key={idx} className="flex justify-between items-center gap-1">
                              <span className="truncate flex-1">{c.campaign}</span>
                              <Badge className="bg-violet-100 text-violet-700 text-[10px]">{c.count}</Badge>
                              <span className="text-[10px] text-gray-400">({c.unique_visitors}u)</span>
                            </div>
                          ))}
                        </div>
                      ) : <p className="text-[10px] text-gray-400 italic">—</p>}
                    </div>
                  </div>
                </CardContent></Card>
              )}

              {/* Conversion Funnel */}
              {funnelData && (
                <Card><CardContent className="p-3">
                  <p className="text-xs font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                    <Send className="h-3.5 w-3.5 text-emerald-500" />Conversion funnel · last {funnelData.days}d
                  </p>
                  {/* 3-stage funnel bar */}
                  <div className="grid grid-cols-3 gap-1 mb-3">
                    {[
                      { label: 'Views', value: funnelData.totals.views, color: '#10B981' },
                      { label: 'CTA Clicks', value: funnelData.totals.clicks, color: '#3B82F6', rate: funnelData.totals.click_rate },
                      { label: 'Form Submits', value: funnelData.totals.submits, color: '#8B5CF6', rate: funnelData.totals.submit_rate },
                    ].map((s, idx) => (
                      <div key={idx} className="rounded-lg p-2.5 text-white" style={{ backgroundColor: s.color }}>
                        <p className="text-[10px] uppercase tracking-wider opacity-90">{s.label}</p>
                        <p className="text-2xl font-bold">{s.value.toLocaleString()}</p>
                        {s.rate !== undefined && <p className="text-[10px] opacity-80">{s.rate}% rate</p>}
                      </div>
                    ))}
                  </div>
                  {funnelData.totals.leads > 0 && (
                    <p className="text-[10px] text-emerald-700 mb-2">→ {funnelData.totals.leads} leads created in CRM</p>
                  )}

                  {/* Per-channel ROI table */}
                  {funnelData.by_source?.length > 0 && (
                    <div className="text-[11px] border rounded overflow-hidden mb-2">
                      <div className="grid grid-cols-12 gap-1 px-2 py-1.5 bg-gray-50 border-b font-semibold text-gray-500 uppercase tracking-wider text-[9px]">
                        <div className="col-span-3">Source</div>
                        <div className="col-span-2 text-right">Views</div>
                        <div className="col-span-2 text-right">Clicks</div>
                        <div className="col-span-2 text-right">Submits</div>
                        <div className="col-span-3 text-right">Click → Submit</div>
                      </div>
                      <div className="max-h-44 overflow-y-auto">
                        {funnelData.by_source.map((row, idx) => (
                          <div key={idx} className="grid grid-cols-12 gap-1 px-2 py-1.5 border-b last:border-0 hover:bg-gray-50">
                            <div className="col-span-3 capitalize font-medium">{row.source}</div>
                            <div className="col-span-2 text-right">{row.views}</div>
                            <div className="col-span-2 text-right text-blue-600">{row.clicks}</div>
                            <div className="col-span-2 text-right text-violet-600">{row.submits}</div>
                            <div className="col-span-3 text-right text-emerald-600 font-mono">
                              {row.click_rate}% / {row.submit_rate}%
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Top CTAs */}
                  {funnelData.top_ctas?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5 mt-2">Top CTAs clicked</p>
                      <div className="space-y-1">
                        {funnelData.top_ctas.map((cta, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs">
                            <span className="flex-1 truncate font-medium">{cta.label}</span>
                            <span className="text-gray-400 text-[10px] truncate max-w-[140px]">{cta.target}</span>
                            <Badge className="bg-blue-100 text-blue-700 text-[10px]">{cta.clicks}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent leads from this page */}
                  {funnelData.recent_leads?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Recent leads ({funnelData.totals.leads})</p>
                      <div className="text-[11px] border rounded overflow-hidden">
                        <div className="grid grid-cols-12 gap-1 px-2 py-1.5 bg-gray-50 border-b font-semibold text-gray-500 uppercase tracking-wider text-[9px]">
                          <div className="col-span-3">When</div>
                          <div className="col-span-3">Name</div>
                          <div className="col-span-3">Email</div>
                          <div className="col-span-3">Source / Campaign</div>
                        </div>
                        <div className="max-h-32 overflow-y-auto">
                          {funnelData.recent_leads.map((l, idx) => (
                            <div key={idx} className="grid grid-cols-12 gap-1 px-2 py-1.5 border-b last:border-0 hover:bg-gray-50">
                              <div className="col-span-3 font-mono text-gray-500 truncate">{l.created_at ? new Date(l.created_at).toLocaleString() : '—'}</div>
                              <div className="col-span-3 truncate font-medium">{l.name || '(no name)'}</div>
                              <div className="col-span-3 truncate text-gray-600">{l.email || '—'}</div>
                              <div className="col-span-3 truncate">
                                <span className="text-emerald-700 capitalize">{l.utm_source}</span>
                                {l.utm_campaign && <span className="text-gray-400"> · {l.utm_campaign}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent></Card>
              )}

              {/* Recent views table */}
              {analyticsData.recent?.length > 0 && (
                <Card><CardContent className="p-3">
                  <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />Recent visits</p>
                  <div className="text-[11px] border rounded overflow-hidden">
                    <div className="grid grid-cols-12 gap-2 px-2 py-1.5 bg-gray-50 border-b font-semibold text-gray-500 uppercase tracking-wider text-[9px]">
                      <div className="col-span-3">When</div>
                      <div className="col-span-2">Device</div>
                      <div className="col-span-3">Referrer</div>
                      <div className="col-span-3">Source / Campaign</div>
                      <div className="col-span-1">Visitor</div>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {analyticsData.recent.map((v, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-2 px-2 py-1.5 border-b last:border-0 hover:bg-gray-50">
                          <div className="col-span-3 font-mono text-gray-500 truncate">{v.at ? new Date(v.at).toLocaleString() : '—'}</div>
                          <div className="col-span-2 capitalize">{v.device}</div>
                          <div className="col-span-3 truncate">{v.referrer}</div>
                          <div className="col-span-3 truncate">
                            {v.utm_source ? (
                              <span className="text-emerald-700"><b className="capitalize">{v.utm_source}</b>{v.utm_campaign ? ` · ${v.utm_campaign}` : ''}</span>
                            ) : <span className="text-gray-400">direct</span>}
                          </div>
                          <div className="col-span-1 font-mono text-gray-400">{v.visitor_id}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent></Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
